import { CircularProgress, TextField } from '@mui/material'
import { RefObject, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { DefectAssistantSuggestion, OptionValue } from '../../../actions/defectAssistant'
import { RoundVM, SuggestionAdvice } from '../../../reducers/createDefectAssistantViewModel'
import { AiButton } from '../CtaButtons'
import { optionKey } from './shared'
import {
  AI_FIELD_SX,
  FinishSection,
  OptionDesc,
  OptionsList,
  OptionTitle,
  SCROLL_DELAY_MS,
  ScrollOptionButton,
  Section,
  SectionQuestion,
  SectionReasoning,
} from './styles'

export const SuggestionsBlock = ({
  round,
  onPick,
  onMeta,
  onExclude,
}: {
  round: RoundVM
  onPick: (s: DefectAssistantSuggestion) => void
  onMeta: (opt: OptionValue) => void
  onExclude: () => void
}): JSX.Element => {
  const { t } = useTranslation()

  const extrasDimmed = round.extrasState !== 'active'
  return (
    <Section>
      {round.showHeader && (
        <SectionQuestion muted={round.headerMuted}>
          {t('defect.aiAssistant.suggestionsHeader')}
        </SectionQuestion>
      )}
      <OptionsList>
        {round.suggestions.map(({ suggestion, label, state, clickable, advice }, idx) => (
          <ScrollOptionButton
            key={`${suggestion.defectCode}-${idx}`}
            type="button"
            optionState={state}
            onClick={() => {
              if (clickable) {
                onPick(suggestion)
              }
            }}
          >
            <OptionTitle dimmed={state === 'secondary'}>{label}</OptionTitle>
            <RenderAdvice advice={advice} dimmed={state === 'secondary'} />
          </ScrollOptionButton>
        ))}
        {round.metaOptions.map((opt, idx) => (
          <ScrollOptionButton
            key={`meta-${optionKey(opt)}-${idx}`}
            type="button"
            optionState={round.extrasState}
            onClick={() => {
              if (round.extrasEnabled) {
                onMeta(opt)
              }
            }}
          >
            <OptionTitle dimmed={extrasDimmed}>{opt.label ?? ''}</OptionTitle>
            {opt.additionalInfo && (
              <OptionDesc dimmed={extrasDimmed}>{opt.additionalInfo}</OptionDesc>
            )}
          </ScrollOptionButton>
        ))}
        {round.showFallbackExclude && (
          <ScrollOptionButton
            type="button"
            optionState={round.extrasState}
            onClick={() => {
              if (round.extrasEnabled) {
                onExclude()
              }
            }}
          >
            <OptionTitle dimmed={extrasDimmed}>
              {t('defect.aiAssistant.noneOfTheseTitle')}
            </OptionTitle>
            <OptionDesc dimmed={extrasDimmed}>
              {t('defect.aiAssistant.noneOfTheseReasoning')}
            </OptionDesc>
          </ScrollOptionButton>
        )}
      </OptionsList>
    </Section>
  )
}

const RenderAdvice = ({
  advice,
  dimmed,
}: {
  advice: SuggestionAdvice
  dimmed: boolean
}): JSX.Element | null => {
  const { t } = useTranslation()
  switch (advice.kind) {
    case 'text':
      return <OptionDesc dimmed={dimmed}>{advice.text}</OptionDesc>
    case 'failed':
      return (
        <OptionDesc dimmed={dimmed}>{t('defect.aiAssistant.defectCodeAdviceFailed')}</OptionDesc>
      )
    case 'loading':
      return (
        <OptionDesc dimmed={dimmed}>
          <CircularProgress size={14} />
        </OptionDesc>
      )
    case 'none':
      return null
  }
}

export const Interlude = ({
  mode,
  detail,
  value,
  onChange,
  onSubmit,
  isScrollAnchor,
}: {
  mode: 'input' | 'loading' | 'locked'
  detail: string
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
  isScrollAnchor: boolean
}): JSX.Element => {
  const { t } = useTranslation()
  const interludeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isScrollAnchor || mode !== 'locked') return
    const timer = setTimeout(() => {
      interludeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, SCROLL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isScrollAnchor, mode])

  return (
    <Section ref={interludeRef}>
      <SectionQuestion muted={mode !== 'input'}>
        {t('defect.aiAssistant.additionalDetailsTitle')}
      </SectionQuestion>
      <SectionReasoning>{t('defect.aiAssistant.additionalDetailsHelp')}</SectionReasoning>
      <TextField
        multiline
        minRows={3}
        fullWidth
        disabled={mode !== 'input'}
        value={mode === 'input' ? value : detail}
        onChange={mode === 'input' ? (e) => onChange(e.target.value) : undefined}
        placeholder={t('defect.aiAssistant.additionalDetailsPlaceholder')}
        sx={AI_FIELD_SX}
      />
      {mode === 'loading' ? (
        <AiButton type="button" disabled>
          <CircularProgress size={20} sx={{ color: 'white' }} />
          {t('defect.aiAssistant.analyzing')}
        </AiButton>
      ) : (
        <AiButton
          type="button"
          disabled={mode === 'locked'}
          onClick={mode === 'input' ? onSubmit : undefined}
        >
          {t('defect.aiAssistant.retryAnalyze')}
        </AiButton>
      )}
    </Section>
  )
}

export const FinishStep = ({
  flowComplete,
  amendLoading,
  activeRef,
  onFinish,
}: {
  flowComplete: boolean
  amendLoading: boolean
  activeRef: RefObject<HTMLDivElement>
  onFinish: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  if (flowComplete) {
    return (
      <div ref={activeRef}>
        <FinishSection>
          <AiButton type="button" disabled={amendLoading} onClick={onFinish}>
            {t('defect.aiAssistant.goToReview')}
          </AiButton>
        </FinishSection>
      </div>
    )
  }
  return (
    <FinishSection style={{ opacity: 0.25 }}>
      <AiButton type="button" disabled>
        {t('defect.aiAssistant.goToReview')}
      </AiButton>
    </FinishSection>
  )
}
